import React, { useId } from 'react';
import { StyledAnchor, StyledButton, StyledCompactNumber, StyledIconContainer, StyledList, StyledListItem, StyledNumber, StyledPaginationContainer, StyledVisuallyHiddenPrefix } from './index.styles.js';
import { Icon } from '../icon/index.js';
import {} from '@oneaudi/unified-web-alpha-common';
import { pickDataProps } from '../../utils/pick-data-props.js';
import { pickA11yProps } from '../../utils/pick-a11y-props.js';
const isValidCurrent = (current, itemCount) => {
    return Math.max(1, Math.min(current, itemCount));
};
const isNormalExtendedVariant = (props) => {
    return props.variant === 'normal' || props.variant === 'extended';
};
export const Pagination = (props) => {
    const id = useId();
    const { variant, className, itemCount, decrementElement, incrementElement, margin, arrowsAlwaysEnabled } = props;
    const validatedCurrent = isValidCurrent(props.current, props.itemCount);
    const ControlElement = ({ element, itemCount, current, type, label, arrowsAlwaysEnabled, variant }) => {
        const icon = (React.createElement(Icon, { name: type === 'decrement' ? 'back' : 'forward', size: "s" }));
        const isDisabled = arrowsAlwaysEnabled
            ? false
            : type === 'decrement'
                ? current <= 1
                : itemCount
                    ? current >= itemCount
                    : false;
        if (element.href) {
            return (React.createElement(StyledAnchor, { onClick: element.onClick, onKeyDown: element.onKeyDown, href: element.href, "aria-label": element['aria-label'], "aria-disabled": element['aria-disabled'], "aria-current": element['aria-current'], hasLabel: !!label, ref: element.ref }, type === 'decrement' ? (React.createElement(React.Fragment, null,
                variant !== 'minimal' ? (icon) : (React.createElement(StyledIconContainer, null, icon)),
                label && React.createElement("span", null, label))) : (React.createElement(React.Fragment, null,
                label && React.createElement("span", null, label),
                variant !== 'minimal' ? (icon) : (React.createElement(StyledIconContainer, null, icon))))));
        }
        return (React.createElement(StyledButton, { variant: variant, onClick: element.onClick, onKeyDown: element.onKeyDown, disabled: isDisabled, "aria-label": element['aria-label'], "aria-disabled": element['aria-disabled'], "aria-current": element['aria-current'], hasLabel: !!label, ref: element.ref }, type === 'decrement' ? (React.createElement(React.Fragment, null,
            variant !== 'minimal' ? (icon) : (React.createElement(StyledIconContainer, null, icon)),
            label && React.createElement("span", null, label))) : (React.createElement(React.Fragment, null,
            label && React.createElement("span", null, label),
            variant !== 'minimal' ? (icon) : (React.createElement(StyledIconContainer, null, icon))))));
    };
    if (variant === 'minimal') {
        return (React.createElement(StyledPaginationContainer, { variant: variant, className: className, hasLabel: !!decrementElement.label && !!incrementElement.label, margin: margin, ...pickA11yProps(props), ...pickDataProps(props) },
            React.createElement(StyledList, { hasLabel: !!decrementElement.label && !!incrementElement.label },
                React.createElement(StyledListItem, null,
                    React.createElement(ControlElement, { variant: variant, itemCount: itemCount, current: validatedCurrent, element: decrementElement, type: "decrement", label: decrementElement.label, arrowsAlwaysEnabled: arrowsAlwaysEnabled })),
                React.createElement(StyledListItem, null,
                    React.createElement(ControlElement, { variant: variant, itemCount: itemCount, current: validatedCurrent, element: incrementElement, type: "increment", label: incrementElement.label, arrowsAlwaysEnabled: arrowsAlwaysEnabled })))));
    }
    if (variant === 'compact') {
        return (React.createElement(StyledPaginationContainer, { variant: variant, className: className, margin: margin, ...pickA11yProps(props), ...pickDataProps(props) },
            React.createElement(StyledList, null,
                React.createElement(StyledListItem, null,
                    React.createElement(ControlElement, { itemCount: itemCount, current: validatedCurrent, element: decrementElement, type: "decrement", arrowsAlwaysEnabled: arrowsAlwaysEnabled })),
                React.createElement(StyledListItem, null,
                    React.createElement(StyledCompactNumber, null,
                        React.createElement("span", null, validatedCurrent),
                        "/",
                        itemCount)),
                React.createElement(StyledListItem, null,
                    React.createElement(ControlElement, { itemCount: itemCount, current: validatedCurrent, element: incrementElement, type: "increment", arrowsAlwaysEnabled: arrowsAlwaysEnabled })))));
    }
    // For 'normal' or 'extended' variant
    const prefix = isNormalExtendedVariant(props) && props.visuallyHiddenItemPrefix ? (React.createElement(StyledVisuallyHiddenPrefix, null, props.visuallyHiddenItemPrefix)) : null;
    return (React.createElement(StyledPaginationContainer, { variant: variant, className: className, margin: margin, ...pickA11yProps(props), ...pickDataProps(props) },
        React.createElement(StyledList, null,
            React.createElement(StyledListItem, null,
                React.createElement(ControlElement, { itemCount: itemCount, current: validatedCurrent, element: decrementElement, type: "decrement", arrowsAlwaysEnabled: arrowsAlwaysEnabled })),
            isNormalExtendedVariant(props) &&
                props.items?.map((item, i) => {
                    const isActive = validatedCurrent === i + 1;
                    // Logic for normal variant to only show 3 items at a time
                    if (variant === 'normal') {
                        const nextItemRange = validatedCurrent === 1
                            ? 2
                            : validatedCurrent === itemCount
                                ? 0
                                : 1;
                        const prevItemRange = validatedCurrent === 1
                            ? 0
                            : validatedCurrent === itemCount
                                ? 2
                                : 1;
                        if (i + 1 > validatedCurrent + nextItemRange ||
                            i + 1 < validatedCurrent - prevItemRange) {
                            return null;
                        }
                    }
                    // Logic for extended variant to show more items with ellipses
                    if (variant === 'extended') {
                        const showEllipsisStart = validatedCurrent >= 5;
                        const showEllipsisEnd = validatedCurrent < itemCount - 3;
                        const minimumAmountForExtended = itemCount > 6;
                        if (minimumAmountForExtended) {
                            if (showEllipsisStart) {
                                if (i === 1) {
                                    return (React.createElement(StyledListItem, { key: `${id}-${i}` },
                                        React.createElement("span", null, "...")));
                                }
                                if (i > 1 && i < validatedCurrent - 2 && i < itemCount - 5) {
                                    return null;
                                }
                            }
                            if (showEllipsisEnd) {
                                if (i === itemCount - 2) {
                                    return (React.createElement(StyledListItem, { key: `${id}-${i}` },
                                        React.createElement("span", null, "...")));
                                }
                                if (i > 4 && i > validatedCurrent && i < itemCount - 2) {
                                    return null;
                                }
                            }
                        }
                    }
                    return (React.createElement(StyledListItem, { key: `${id}-${i}`, isActive: isActive }, item.href ? (React.createElement(StyledAnchor, { "aria-current": item['aria-current'] || isActive, href: item.href, "aria-label": item['aria-label'], onKeyDown: item.onKeyDown, onClick: item.onClick },
                        prefix,
                        React.createElement(StyledNumber, null, i + 1))) : (React.createElement(StyledButton, { "aria-current": item['aria-current'] || isActive, onClick: item.onClick, "aria-label": item['aria-label'], onKeyDown: item.onKeyDown },
                        prefix,
                        React.createElement(StyledNumber, null, i + 1)))));
                }),
            React.createElement(StyledListItem, null,
                React.createElement(ControlElement, { itemCount: itemCount, current: validatedCurrent, element: incrementElement, type: "increment", arrowsAlwaysEnabled: arrowsAlwaysEnabled })))));
};
//# sourceMappingURL=index.js.map